/* eslint-disable react/prop-types,react/jsx-key,react/jsx-indent,react/jsx-indent-props */
import React, {forwardRef} from 'react';
import {Alert} from '@material-ui/lab';
import {
    useTable,
    useSortBy,
    useFilters,
    usePagination
} from 'react-table';

import {School} from '../../../../api/types';
import {HStack} from '../../../layout/layout.styled';
import {SchoolAcademicYearTableColumn} from '../../academic-year-search-form';
import {useEffectAfterMount} from '../../../../hooks/use-effect-after-mount/use-effect-after-mount';
import {Pagination} from '../pagination';
import {
    SortArrowsIcon, TableElement, THead, Container, SortIconContainer, LoadingSpinnerOverlay
} from './school-academic-year-table.styled';

export type TableViewProps = {
    columns: SchoolAcademicYearTableColumn[]
    data: School[]
    isError?: boolean
    isFetching?: boolean
    isPreviousData?: boolean
    pageSize: number
    pageIndex: number
    totalSchoolsFound: number
    totalPages: number
    onPaginationChange: (options: PaginationOptions) => any
}

export type PaginationOptions = {
    pageIndex: number
    pageSize: number
}

export type TableRef = {
    setAllFilters: (filters: Array<{id: string, value: any}>) => void
    setFilter: (columnId: string, filterValue: string) => void
    filters: Array<{id: string, value: any}>
    setPageSize: (pageSize: number) => any
    gotoPage: (pageIndex: number) => any
}

// eslint-disable-next-line react/display-name
export const SchoolAcademicYearTable = forwardRef<TableRef, TableViewProps>(({
    onPaginationChange,
    data,
    totalSchoolsFound,
    totalPages,
    columns,
    pageSize: controlledPageSize,
    pageIndex: controlledPageIndex,
    isError = false,
    isFetching = false,
    isPreviousData = false
}, ref) => {
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        rows,
        // https://react-table.tanstack.com/docs/api/useFilters#usefilters
        // @ts-ignore - useTable generics for useFilters?
        setFilter,
        setAllFilters,
        // https://react-table.tanstack.com/docs/api/usePagination#usepagination
        // @ts-ignore - useTable generics for usePagination?
        page,
        pageOptions,
        pageCount,
        nextPage,
        previousPage,
        setPageSize,
        gotoPage,
        state: {
            filters,
            pageIndex,
            pageSize
        }
    } = useTable<School>({
        columns,
        data,
        // https://react-table.tanstack.com/docs/api/useSortBy#usesortby
        // @ts-ignore - useTable generics for useSortBy?
        disableMultiSort: true,
        defaultCanSort: false,
        // https://react-table.tanstack.com/docs/api/useFilters#usefilters
        // @ts-ignore - useTable generics for useFilters?
        defaultColumn: {},
        filterTypes: {},
        // https://react-table.tanstack.com/docs/api/usePagination#usepagination
        manualPagination: true,
        pageCount: totalSchoolsFound,
        // 10 is default in react-table
        initialState: {
            pageIndex: controlledPageIndex,
            pageSize: controlledPageSize
        } as any
    },
    useFilters,
    useSortBy,
    usePagination
    ) as any;

    useEffectAfterMount(() => {
        onPaginationChange({pageIndex, pageSize});
    }, [pageIndex, pageSize]);

    // @ts-ignore // todo type ref properly
    ref.current = {
        gotoPage,
        setPageSize,
        setAllFilters,
        setFilter,
        filters
    };

    return (
        <>
            <Container
                isFetching={isFetching}
                isPreviousData={isPreviousData}
                data-testid={'SchoolAcademicYearTableView'}
            >
                {isFetching && isPreviousData ? <LoadingSpinnerOverlay/> : null}
                {rows.length > 0 && <TableElement {...getTableProps()}>
                    <THead>
                        {headerGroups.map((headerGroup: any) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column: any) => {
                                    return <th {...column.getHeaderProps()}>
                                        <HStack justifyContent={'space-between'}
                                            spacing={'10px'}
                                        >
                                            <span
                                                data-testid={`column-id-${column.id}`}>
                                                {column.render('Header')}
                                            </span>
                                            <div>{column.canFilter ? column.render('Filter') : null}</div>

                                            {column.defaultCanSort &&
                                            <SortIconContainer
                                                data-testid={'SortButton'}
                                                role={'button'}
                                                {...column.getSortByToggleProps()}
                                            >
                                                {column.defaultCanSort ? column.isSorted ? '' :
                                                    <SortArrowsIcon/> : ''}

                                                {column.isSorted
                                                    ? column.isSortedDesc
                                                        ?
                                                        <SortArrowsIcon
                                                            up={false}/>
                                                        : <SortArrowsIcon
                                                            down={false}/>
                                                    : ''}
                                            </SortIconContainer>}
                                        </HStack>
                                    </th>
                                })}
                            </tr>
                        ))}
                    </THead>

                    <tbody {...getTableBodyProps()}>
                        {page.map((row: any) => {
                            prepareRow(row);
                            return (
                                <tr
                                    {...row.getRowProps()}
                                    data-testid={'Row'}
                                    data-schoolid={row.original.schoolId}
                                    data-academicyearid={row.original.academicYear?.academicYearId || 'none'}
                                >
                                    {row.cells.map((cell: any) => {
                                        return (
                                            <td {...cell.getCellProps()}>
                                                {cell.render('Cell')}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </TableElement>}

                {rows.length == 0 ?
                    <Alert data-testid={'ErrorInfo'} severity={isError ? 'error' : 'info'}>
                        {isError ? 'Error' : 'No schools found'}
                    </Alert> : null}
            </Container>

            {rows.length > 0 ? <Pagination
                gotoPage={gotoPage}
                canPreviousPage={controlledPageIndex > 1}
                previousPage={previousPage}
                nextPage={nextPage}
                canNextPage={controlledPageIndex < totalPages}
                pageOptions={pageOptions}
                totalResults={totalSchoolsFound}
                pageIndex={controlledPageIndex}
                totalPages={totalPages}
                pageCount={pageCount}
            /> : null}
        </>
    );
});
